interface PerformanceProfile {
  isLowSpec: boolean;
  hasLimitedMemory: boolean;
  renderTimeout: number;
  maxDataPoints: number;
  shouldSkipComplexCharts: boolean;
}

interface ChartComplexity {
  dataPointCount: number;
  isComplex: boolean;
  estimatedRenderTime: number;
}

// Detect device performance capabilities
export const getPerformanceProfile = (): PerformanceProfile => {
  // Default safe values
  let isLowSpec = false;
  let hasLimitedMemory = false;
  
  try {
    // Check CPU cores
    const cores = (navigator as any).hardwareConcurrency || 2;
    isLowSpec = cores < 4;
    
    // Check available memory (if supported)
    const deviceMemory = (navigator as any).deviceMemory;
    if (deviceMemory !== undefined) {
      hasLimitedMemory = deviceMemory < 4; // Less than 4GB
    }
    
    // Check if we're on a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      isLowSpec = true;
      hasLimitedMemory = true;
    }
    
  } catch (error) {
    // If we can't detect, assume low spec for safety
    console.warn('Unable to detect device performance, assuming low spec:', error);
    isLowSpec = true;
    hasLimitedMemory = true;
  }
  
  return {
    isLowSpec,
    hasLimitedMemory,
    renderTimeout: isLowSpec ? 8000 : 15000, // Shorter timeout for low spec
    maxDataPoints: isLowSpec ? 50 : 200,
    shouldSkipComplexCharts: isLowSpec && hasLimitedMemory
  };
};

// Analyze chart complexity
export const analyzeChartComplexity = (chartData: any): ChartComplexity => {
  let dataPointCount = 0;
  
  try {
    if (chartData.datasets && Array.isArray(chartData.datasets)) {
      for (const dataset of chartData.datasets) {
        if (dataset.data && Array.isArray(dataset.data)) {
          dataPointCount += dataset.data.length;
        }
      }
    }
    
    // Consider chart type complexity
    const complexChartTypes = ['scatter', 'bubble', 'radar', 'polarArea'];
    const isComplexType = complexChartTypes.includes(chartData.type);
    
    // Determine if chart is complex
    const isComplex = dataPointCount > 100 || isComplexType || 
                     (chartData.datasets && chartData.datasets.length > 5);
    
    // Estimate render time (rough approximation)
    let estimatedRenderTime = Math.max(1000, dataPointCount * 10);
    if (isComplexType) {
      estimatedRenderTime *= 1.5;
    }
    
    return {
      dataPointCount,
      isComplex,
      estimatedRenderTime
    };
    
  } catch (error) {
    console.warn('Error analyzing chart complexity:', error);
    return {
      dataPointCount: 999, // Assume high complexity on error
      isComplex: true,
      estimatedRenderTime: 10000
    };
  }
};

// Determine if a chart should be rendered based on performance and complexity
export const shouldRenderChart = (chartData: any): boolean => {
  const performance = getPerformanceProfile();
  const complexity = analyzeChartComplexity(chartData);
  
  // Always skip if we can't analyze the data
  if (!chartData || !chartData.type) {
    return false;
  }
  
  // Skip complex charts on low-spec devices
  if (performance.shouldSkipComplexCharts && complexity.isComplex) {
    console.warn(`Skipping complex chart (${chartData.type}) with ${complexity.dataPointCount} data points on low-spec device`);
    return false;
  }
  
  // Skip charts with too many data points
  if (complexity.dataPointCount > performance.maxDataPoints) {
    console.warn(`Skipping chart with ${complexity.dataPointCount} data points (max: ${performance.maxDataPoints})`);
    return false;
  }
  
  return true;
};

// Create a promise that renders a chart with timeout
export const renderChartWithTimeout = <T>(
  renderFunction: () => Promise<T>,
  timeoutMs: number = getPerformanceProfile().renderTimeout
): Promise<T | null> => {
  return Promise.race([
    renderFunction(),
    new Promise<null>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Chart rendering timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    })
  ]).catch((error) => {
    console.warn('Chart rendering failed or timed out:', error);
    return null;
  });
};

// Memory usage estimation
export const estimateChartMemoryUsage = (chartData: any): number => {
  try {
    const complexity = analyzeChartComplexity(chartData);
    // Rough estimation: each data point uses ~100 bytes, complex charts use more
    const baseMemory = complexity.dataPointCount * 100;
    const complexityMultiplier = complexity.isComplex ? 2 : 1;
    return baseMemory * complexityMultiplier;
  } catch (error) {
    return 1000000; // 1MB default if we can't estimate
  }
};

// Check if browser has enough memory for chart
export const hasEnoughMemoryForChart = (chartData: any): boolean => {
  try {
    const estimatedUsage = estimateChartMemoryUsage(chartData);
    const performance = getPerformanceProfile();
    
    // If we can't detect memory, be conservative
    if (performance.hasLimitedMemory) {
      return estimatedUsage < 500000; // 500KB limit for low memory devices
    }
    
    return estimatedUsage < 5000000; // 5MB limit for normal devices
  } catch (error) {
    return false; // Err on the side of caution
  }
}; 